import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import { lastValueFrom } from 'rxjs'
import { environment } from 'src/environments/environment'

const BRAND_LOGO_PATH = 'assets/brand/logo.png'
const AYB_S3_PATH = `${environment.alimentosBebidasApi}/api/v1`

interface PdfGenerationOptions {
  content: any[]
  headers: string[]
  metadata: {
    fileNumber?: string
    dateRange?: string
    lstPax?: any[]
    totalMenuXFilaAYB?: number
    productName?: string
    serviceName?: string
    menu?: string
    cocineros?: string[]
    serviceId?: string
  }
  pdfOptions?: {
    filename?: string
    headerColor?: [number, number, number]
    startY?: number
    margin?: { top: number }
  }
}

@Injectable({
  providedIn: 'root'
})
export class PdfGeneratorService {
  constructor (private readonly http: HttpClient) {}

  private _redondearNumero (num: number): number {
    return Number.isInteger(num) ? num : parseFloat(num.toFixed(1))
  }

  async generatePdf (options: PdfGenerationOptions): Promise<string> {
    const { content, headers, metadata, pdfOptions = {} } = options

    const {
      filename = 'lista-insumos.pdf',
      headerColor = [233, 107, 26],
      startY = 80,
      margin = { top: 10 }
    } = pdfOptions

    const documentPdf = new jsPDF()

    this._addHeader(documentPdf, metadata)
    this._addMetadataSection(documentPdf, metadata)
    this._addTable(documentPdf, content, headers, {
      headerColor,
      startY,
      margin
    })

    documentPdf.save(filename)

    const base64Data = documentPdf.output('datauristring')

    if (metadata.serviceId) {
      await this._uploadToS3(base64Data, metadata.serviceId)
    }

    return base64Data
  }

  private _addHeader (pdf: jsPDF, metadata: any): void {
    pdf.addImage(BRAND_LOGO_PATH, 'PNG', 140, 15, 60, 15)
    pdf.setFontSize(13)
    pdf.text('LISTA DE INSUMOS', 10, 70)
  }

  private _addMetadataSection (pdf: jsPDF, metadata: any): void {
    pdf.setFontSize(10)
    let yPosition = 15

    const metadataFields = [
      { label: 'Número de File', value: metadata.fileNumber },
      { label: 'Fecha de file', value: metadata.dateRange },
      { label: 'Cantidad de pax', value: metadata.lstPax?.length },
      { label: 'Total', value: metadata.totalMenuXFilaAYB },
      { label: 'Ruta', value: metadata.productName },
      { label: 'Servicio', value: metadata.serviceName },
      { label: 'Menú', value: metadata.menu }
    ]

    metadataFields.forEach(field => {
      pdf.text(`${field.label}: ${field.value || '-'}`, 10, yPosition)
      yPosition += 5
    })

    metadata.cocineros?.forEach((cocinero: string) => {
      pdf.text(`Cocinero: ${cocinero}`, 10, yPosition)
      yPosition += 5
    })

    pdf.line(10, yPosition + 5, 200, yPosition + 5)
  }
  private _addTable (
    pdf: jsPDF,
    content: any[],
    headers: string[],
    options: {
      headerColor: [number, number, number]
      startY: number
      margin: any
    }
  ): void {
    const columnsToDisplay = [
      'nombreInsumo',
      'unidadMedida',
      'cantidadTotal',
      'costo',
      'costoTotal'
    ]

    const body = content.map(obj =>
      columnsToDisplay.map(key =>
        this._formatCellValue(key, this._getValueFromObject(obj, key))
      )
    )

    const totalFinal = content.reduce(
      (sum, item) => sum + Number(item.costoTotal),
      0
    )

    autoTable(pdf, {
      startY: options.startY,
      head: [headers],
      body: body,
      margin: options.margin,
      headStyles: { fillColor: options.headerColor },
      didDrawPage: (data: any) => {
        const finalY = data.cursor.y + 5
        pdf.setFontSize(12)
        pdf.setFont('helvetica', 'bold')
        pdf.text(
          `Total Final: ${this._redondearNumero(totalFinal)}`,
          150,
          finalY
        )
      }
    })
  }

  private async _uploadToS3 (
    base64Data: string,
    serviceId: string
  ): Promise<void> {
    try {
      const response = await lastValueFrom(
        this.http.post(`${AYB_S3_PATH}/addFileOrdenSalida`, {
          data: base64Data,
          ordenSalidaId: serviceId
        })
      )

      console.log('Upload successful:', response)
    } catch (error) {
      console.error('Upload failed:', error)
      throw error
    }
  }
  private _formatCellValue (key: string, value: any): string {
    if (value == null) return '-'
    if (key === 'cantidadTotal' && typeof value === 'number') {
      return this._redondearNumero(value).toString()
    }
    return value.toString()
  }

  private _getValueFromObject (obj: any, key: string): any {
    // Handle the case where 'nombreInsumo' might be stored as 'nombre'
    if (key === 'nombreInsumo') {
      return obj.nombreInsumo || obj.nombre || obj.name
    }
    return obj[key]
  }
}
